import { useMemo, useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { SegmentedControl } from '../../../components/ui/Tabs';
import { money, formatDate, formatMonthKey, formatShortDate, planDisplayName } from './formatters';
import { PanelTitle } from './DataIssues';
import type { ContractRow } from '../../../api/finance';

/** Text color expressing how urgent a contract's expiry is. */
function urgencyText(daysLeft: number | null): string {
  if (daysLeft === null || daysLeft < 0) return 'text-text-tertiary';
  if (daysLeft <= 30) return 'text-error';
  if (daysLeft <= 90) return 'text-warning';
  return 'text-text-secondary';
}

function urgencyDot(daysLeft: number): string {
  if (daysLeft <= 30) return 'bg-error';
  if (daysLeft <= 90) return 'bg-warning';
  return 'bg-text-primary';
}

/** The key under the strip: what each dot colour means, same thresholds as {@link urgencyDot}. */
const URGENCY_KEY = [
  { dot: 'bg-error', label: 'Ends within 30 days' },
  { dot: 'bg-warning', label: '31–90 days' },
  { dot: 'bg-text-primary', label: 'Later' },
] as const;

const MAX_TIMELINE_DOTS = 4;

/** "€14,075 MRR", or the contract's own currency when it has no EUR rate. */
function contractMrr(c: ContractRow): string {
  return c.mrrEur === null ? `${money(c.sourceMrr, c.sourceCurrency)} MRR, unconverted` : `${money(c.mrrEur, 'EUR')} MRR`;
}

/** One dot's hover: "Xerox Corporation · €14,075 MRR · ends 30 Jun". */
function dotTitle(c: ContractRow): string {
  const renewed = c.renewedOrders > 0 ? ` (after ${c.renewedOrders} renewal${c.renewedOrders === 1 ? '' : 's'})` : '';
  return `${c.companyName} · ${contractMrr(c)} · ends ${formatShortDate(c.expiryDate!)}${renewed}`;
}

/** "Acme · €2,000 MRR · ends 12 Oct", one per line: the hover text that says whose dots these are. */
function contractLines(contracts: ContractRow[]): string {
  return contracts
    .slice()
    .sort((a, b) => a.expiryDate!.localeCompare(b.expiryDate!))
    .map(dotTitle)
    .join('\n');
}

/** The month column's tooltip: every customer ending that month, then the click hint. */
function monthTitle(monthKey: string, expiring: ContractRow[]): string {
  if (expiring.length === 0) return `No contracts ending in ${formatMonthKey(monthKey)}`;
  const head = `${expiring.length} contract${expiring.length === 1 ? '' : 's'} ending in ${formatMonthKey(monthKey)}:`;
  return `${head}\n${contractLines(expiring)}\n\nClick for the MRR they carry`;
}

const TIMELINE_RANGES = [1, 3, 6, 12] as const;
type TimelineRange = (typeof TIMELINE_RANGES)[number];
const RANGE_OPTIONS = TIMELINE_RANGES.map((r) => ({ id: String(r) as `${TimelineRange}`, label: `${r}m` }));

const TIMELINE_GRID_COLS: Record<TimelineRange, string> = {
  1: 'grid-cols-1',
  3: 'grid-cols-3',
  6: 'grid-cols-6',
  12: 'grid-cols-12',
};


/** "Arendt Digital Services · €2,500 · 22 days": the near expiries, readable at a glance. */
function EndingSoon({ contracts, months }: { contracts: ContractRow[]; months: string[] }) {
  // The same window as the strip below: this month through the chip's last month.
  const windowLabel = `the next ${months.length === 1 ? 'month' : `${months.length} months`}`;
  const inWindow = new Set(months);
  const soon = contracts
    .filter((c) => c.daysLeft !== null && c.daysLeft >= 0 && c.expiryDate !== null && inWindow.has(c.expiryDate.slice(0, 7)))
    .sort((a, b) => a.daysLeft! - b.daysLeft!);
  if (soon.length === 0) {
    return <p className="mb-3 text-body-xs text-text-tertiary">No contract ends in {windowLabel}.</p>;
  }
  return (
    <>
    <p className="mb-1 text-body-xs font-medium text-text-secondary">Ending in {windowLabel}</p>
    <ul className="mb-3 divide-y divide-border-light border-b border-border-light">
      {soon.map((c) => (
        <li key={c.id} className="flex items-center justify-between gap-3 py-1.5 text-body-sm">
          <span className="flex min-w-0 items-center gap-2">
            <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${urgencyDot(c.daysLeft!)}`} />
            <span className="truncate text-text-primary" title={c.odooName ? `${c.companyName} (${c.odooName})` : c.companyName}>
              {c.companyName}
            </span>
          </span>
          <span className="shrink-0 tabular-nums text-text-secondary">
            {c.mrrEur === null ? `${money(c.sourceMrr, c.sourceCurrency)} unconverted` : money(c.mrrEur, 'EUR')}
            <span className={`ml-2 text-body-xs ${urgencyText(c.daysLeft)}`}>
              {c.daysLeft === 0 ? 'ends today' : `${c.daysLeft} day${c.daysLeft === 1 ? '' : 's'}`} · {formatShortDate(c.expiryDate!)}
            </span>
          </span>
        </li>
      ))}
    </ul>
    </>
  );
}

/**
 * Contract expiries over the coming months: range selector (1/3/6/12m),
 * one dot per expiring contract, click a month for the contract details.
 */
export function ExpiryTimeline({ contracts }: { contracts: ContractRow[] }) {
  const [range, setRange] = useState<TimelineRange>(3);
  const [openMonth, setOpenMonth] = useState<string | null>(null);

  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: range }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
  }, [range]);

  const byMonth = new Map<string, ContractRow[]>(months.map((m) => [m, []]));
  for (const c of contracts) {
    if (c.expiryDate === null || c.daysLeft === null || c.daysLeft < 0) continue;
    byMonth.get(c.expiryDate.slice(0, 7))?.push(c);
  }

  // Only the colours on screen get a legend entry.
  const shown = [...byMonth.values()].flat();
  const legend = URGENCY_KEY.filter((item) => shown.some((c) => urgencyDot(c.daysLeft!) === item.dot));
  const openContracts = openMonth ? (byMonth.get(openMonth) ?? []) : [];
  const toggleMonth = (m: string) => setOpenMonth((cur) => (cur === m ? null : m));

  return (
    <Card padding="md">
      <PanelTitle
        title="Contract expiries"
        info={`The list names every contract ending in the months the chip covers (this month to the last month shown). Below it, one dot per contract ending that month: red within 30 days, amber from 31 to 90 days, so a renewal shows amber a month before it reaches the list. A contract renewed by a later order that starts by the day after it ends runs on to that order's end, so it shows once, at the end of the last renewal. A month shows up to ${MAX_TIMELINE_DOTS} dots; "+2" means two more contracts end that month. Hover a dot for the customer and MRR, click a month for the list.`}
        action={
          <SegmentedControl
            ariaLabel="Expiry window"
            options={RANGE_OPTIONS}
            value={String(range) as `${TimelineRange}`}
            onChange={(id) => {
              setRange(Number(id) as TimelineRange);
              setOpenMonth(null);
            }}
          />
        }
      />
      <EndingSoon contracts={contracts} months={months} />
      <div className={`grid ${TIMELINE_GRID_COLS[range]} gap-1`}>
        {months.map((m) => {
          const expiring = byMonth.get(m) ?? [];
          const isOpen = openMonth === m;
          return (
            <button
              key={m}
              type="button"
              onClick={() => toggleMonth(m)}
              title={monthTitle(m, expiring)}
              aria-label={monthTitle(m, expiring)}
              className={`flex flex-col items-center justify-end gap-1 min-h-16 rounded-md py-1 cursor-pointer ${
                isOpen ? 'bg-surface-secondary' : 'hover:bg-surface-secondary/50'
              }`}
            >
              {expiring.length > MAX_TIMELINE_DOTS && (
                <span className="text-body-xs text-text-tertiary leading-none">
                  +{expiring.length - MAX_TIMELINE_DOTS}
                  <span className="sr-only"> more</span>
                </span>
              )}
              {expiring.slice(0, MAX_TIMELINE_DOTS).map((c) => (
                <span
                  key={c.id}
                  title={dotTitle(c)}
                  className={`h-2 w-2 rounded-full ${urgencyDot(c.daysLeft!)}`}
                />
              ))}
              <span className="text-body-xs text-text-tertiary mt-1">{formatMonthKey(m)}</span>
            </button>
          );
        })}
      </div>
      {legend.length > 0 && (
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-body-xs text-text-tertiary">
        {legend.map((item) => (
          <span key={item.label} className="flex items-center gap-1.5">
            <span aria-hidden className={`h-2 w-2 rounded-full ${item.dot}`} />
            {item.label}
          </span>
        ))}
      </div>
      )}
      {openMonth && (
        <div className="mt-3 border-t border-border-light pt-3">
          <p className="text-body-xs text-text-tertiary mb-1.5">
            Expiring in {formatMonthKey(openMonth)}
            {openContracts.length > 0 &&
              ` · ${money(
                openContracts.reduce((sum, c) => sum + (c.mrrEur ?? 0), 0),
                'EUR',
              )} MRR${openContracts.some((c) => c.mrrEur === null) ? '+' : ''}`}
          </p>
          {openContracts.length === 0 ? (
            <p className="text-body-sm text-text-tertiary">No contracts expiring this month.</p>
          ) : (
            <ul className="divide-y divide-border-light">
              {openContracts
                .slice()
                .sort((a, b) => a.expiryDate!.localeCompare(b.expiryDate!))
                .map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <p className="text-body-sm text-text-primary truncate">{c.companyName}</p>
                      {c.odooName && <p className="text-body-xs text-text-tertiary truncate">{c.odooName}</p>}
                      <p className="text-body-xs text-text-tertiary truncate">{planDisplayName(c.planName)}</p>
                    </div>
                    <div className="flex flex-col items-end shrink-0">
                      <span className="text-body-sm text-text-primary font-medium tabular-nums">
                        {c.mrrEur === null
                          ? `${money(c.sourceMrr, c.sourceCurrency)} · unconverted`
                          : money(c.mrrEur, 'EUR')}
                      </span>
                      <span className={`text-body-xs ${urgencyText(c.daysLeft)}`}>
                        {formatDate(c.expiryDate!)} · {c.daysLeft}d
                      </span>
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
